import { useCallback, useEffect, useId } from "react";
import { create } from "zustand";

interface NativePdfOverlayStore {
  /** One id per DOM overlay that is open right now. */
  openOverlayIds: ReadonlySet<string>;
  setOverlayOpen: (id: string, open: boolean) => void;
}

/**
 * DOM overlays that have to be visible on top of a native PDF view.
 *
 * PDFKit's `PDFView` is an AppKit sibling layered above the whole webview, so a popover or context
 * menu the DOM opens over the viewer is drawn *behind* it. While any of these overlays is open the
 * portal gives the native view a zero-size frame, which hides it without unmounting — the
 * document, its scroll position and its find state all survive the menu.
 */
export const useNativePdfOverlayStore = create<NativePdfOverlayStore>()((set) => ({
  openOverlayIds: new Set<string>(),
  setOverlayOpen: (id, open) =>
    set((state) => {
      if (state.openOverlayIds.has(id) === open) return state;
      const next = new Set(state.openOverlayIds);
      if (open) next.add(id);
      else next.delete(id);
      return { openOverlayIds: next };
    }),
}));

export const useNativePdfHiddenForOverlay = () =>
  useNativePdfOverlayStore((state) => state.openOverlayIds.size > 0);

/**
 * Returns an `onOpenChange` handler that hides the native PDF view for as long as this overlay is
 * open. Keyed per component instance so overlapping overlays (a menu opened from inside another)
 * cannot un-hide the view while one of them is still up.
 */
export function useNativePdfOverlayOpenChange() {
  const id = useId();
  const setOverlayOpen = useNativePdfOverlayStore((state) => state.setOverlayOpen);

  // Radix never reports a close for an overlay whose trigger unmounts while open — and thumbnails
  // are virtualized, so scrolling the rail can do exactly that.
  useEffect(() => () => setOverlayOpen(id, false), [id, setOverlayOpen]);

  return useCallback((open: boolean) => setOverlayOpen(id, open), [id, setOverlayOpen]);
}
